import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  LayoutDashboard, Package, FolderOpen, Image, FileText, 
  Phone, LogOut, Menu, X, Plus, Edit, Trash2, 
  Eye, Upload, Save, MessageSquare
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import {
  getCategories, addCategory, updateCategory, deleteCategory,
  getProducts, addProduct, updateProduct, deleteProduct,
  getBanners, addBanner, updateBanner, deleteBanner,
  getPageContent, updatePageContent,
  getContactInfo, updateContactInfo,
  getContactMessages, markMessageRead, deleteContactMessage,
  uploadImage
} from '../services/firebaseService';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_pharma-hub-85/artifacts/uc3c8jaq_43afdbdc-0274-4dad-a371-a86aa1080642_removalai_preview.png";

const menuItems = [
  { path: '/admin', icon: LayoutDashboard, label: 'İdarə Paneli', exact: true },
  { path: '/admin/categories', icon: FolderOpen, label: 'Kateqoriyalar' },
  { path: '/admin/products', icon: Package, label: 'Məhsullar' },
  { path: '/admin/banners', icon: Image, label: 'Bannerlər' },
  { path: '/admin/pages', icon: FileText, label: 'Səhifələr' },
  { path: '/admin/contact', icon: Phone, label: 'Əlaqə Məlumatı' },
  { path: '/admin/messages', icon: MessageSquare, label: 'Mesajlar' },
];

// Sidebar Component
const Sidebar = ({ isOpen, onClose }) => {
  const location = useLocation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  return (
    <>
      {isOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={onClose} />
      )}
      
      <aside className={`fixed top-0 left-0 h-full w-64 bg-[#1e3a5f] text-white z-50 transform transition-transform lg:translate-x-0 ${
        isOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        <div className="flex flex-col h-full">
          <div className="p-6 border-b border-white/10">
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Logo" className="h-12 w-auto object-contain bg-white rounded-lg p-1" />
              <div>
                <h1 className="font-bold">PolyMax</h1>
                <p className="text-xs text-white/60">Admin Panel</p>
              </div>
            </div>
          </div>

          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {menuItems.map((item) => {
              const isActive = item.exact 
                ? location.pathname === item.path 
                : location.pathname.startsWith(item.path);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={onClose}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                    isActive ? 'bg-white/20 text-white' : 'text-white/70 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  <item.icon className="h-5 w-5" />
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </nav>

          <div className="p-4 border-t border-white/10">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                {user?.name?.[0]?.toUpperCase() || 'A'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{user?.name || 'Admin'}</p>
                <p className="text-xs text-white/60 truncate">{user?.email}</p>
              </div>
            </div>
            <Button 
              variant="ghost" 
              className="w-full justify-start text-white/70 hover:text-white hover:bg-white/10"
              onClick={handleLogout}
            >
              <LogOut className="h-5 w-5 mr-3" />
              Çıxış
            </Button>
          </div>
        </div>
      </aside>
    </>
  );
};

// Dashboard Page
const DashboardPage = () => {
  const [stats, setStats] = useState({ categories: 0, products: 0, banners: 0, messages: 0 });

  useEffect(() => { fetchStats(); }, []);

  const fetchStats = async () => {
    try {
      const [categories, products, banners, messages] = await Promise.all([
        getCategories(),
        getProducts(),
        getBanners(),
        getContactMessages(),
      ]);
      setStats({
        categories: categories.length,
        products: products.length,
        banners: banners.length,
        messages: messages.filter(m => !m.is_read).length,
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  return (
    <div>
      <h1 className="text-2xl font-bold text-[#1e3a5f] mb-6">İdarə Paneli</h1>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="card-hover">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Kateqoriyalar</p>
                <p className="text-3xl font-bold text-[#1e3a5f]">{stats.categories}</p>
              </div>
              <FolderOpen className="h-8 w-8 text-[#1e3a5f]/30" />
            </div>
          </CardContent>
        </Card>
        <Card className="card-hover">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Məhsullar</p>
                <p className="text-3xl font-bold text-[#3b82f6]">{stats.products}</p>
              </div>
              <Package className="h-8 w-8 text-[#3b82f6]/30" />
            </div>
          </CardContent>
        </Card>
        <Card className="card-hover">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Bannerlər</p>
                <p className="text-3xl font-bold text-[#25d366]">{stats.banners}</p>
              </div>
              <Image className="h-8 w-8 text-[#25d366]/30" />
            </div>
          </CardContent>
        </Card>
        <Card className="card-hover">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Yeni Mesajlar</p>
                <p className="text-3xl font-bold text-purple-600">{stats.messages}</p>
              </div>
              <MessageSquare className="h-8 w-8 text-purple-600/30" />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

// Categories Page
const CategoriesPage = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({ name: '', description: '', image_url: '', order: 0 });

  useEffect(() => { fetchCategories(); }, []);

  const fetchCategories = async () => {
    try {
      const data = await getCategories();
      setCategories(data);
    } catch (error) {
      toast.error('Kateqoriyalar yüklənə bilmədi');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCategory) {
        await updateCategory(editingCategory.id, formData);
        toast.success('Kateqoriya yeniləndi');
      } else {
        await addCategory(formData);
        toast.success('Kateqoriya yaradıldı');
      }
      setDialogOpen(false);
      setEditingCategory(null);
      setFormData({ name: '', description: '', image_url: '', order: 0 });
      fetchCategories();
    } catch (error) {
      toast.error('Əməliyyat uğursuz oldu');
    }
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setFormData({ name: category.name, description: category.description || '', image_url: category.image_url || '', order: category.order || 0 });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu kateqoriyanı silmək istədiyinizə əminsiniz?')) return;
    try {
      await deleteCategory(id);
      toast.success('Kateqoriya silindi');
      fetchCategories();
    } catch (error) {
      toast.error('Silinmə uğursuz oldu');
    }
  };

  if (loading) return <div className="animate-spin rounded-full h-8 w-8 border-4 border-[#1e3a5f] border-t-transparent mx-auto"></div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-[#1e3a5f]">Kateqoriyalar</h1>
        <Button onClick={() => { setEditingCategory(null); setFormData({ name: '', description: '', image_url: '', order: 0 }); setDialogOpen(true); }} className="bg-[#1e3a5f]">
          <Plus className="h-4 w-4 mr-2" /> Yeni Kateqoriya
        </Button>
      </div>

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Ad</TableHead>
              <TableHead>Təsvir</TableHead>
              <TableHead>Sıra</TableHead>
              <TableHead className="text-right">Əməliyyatlar</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {categories.map((category) => (
              <TableRow key={category.id}>
                <TableCell className="font-medium">{category.name}</TableCell>
                <TableCell className="max-w-xs truncate">{category.description}</TableCell>
                <TableCell>{category.order}</TableCell>
                <TableCell className="text-right space-x-2">
                  <Button variant="ghost" size="icon" onClick={() => handleEdit(category)}><Edit className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => handleDelete(category.id)} className="text-red-500"><Trash2 className="h-4 w-4" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingCategory ? 'Kateqoriyanı Redaktə Et' : 'Yeni Kateqoriya'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Ad</Label>
              <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required />
            </div>
            <div className="space-y-2">
              <Label>Təsvir</Label>
              <Textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} />
            </div>
            <div className="space-y-2">
              <Label>Sıra</Label>
              <Input type="number" value={formData.order} onChange={(e) => setFormData({...formData, order: parseInt(e.target.value) || 0})} />
            </div>
            <DialogFooter>
              <Button type="submit" className="bg-[#1e3a5f]"><Save className="h-4 w-4 mr-2" /> Yadda Saxla</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Products Page
const ProductsPage = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({ 
    name: '', description: '', category_id: '', image_url: '', 
    images: [], price: '', show_price: false, features: [], order: 0 
  });
  const [featureInput, setFeatureInput] = useState('');

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [productsData, categoriesData] = await Promise.all([getProducts(), getCategories()]);
      setProducts(productsData);
      setCategories(categoriesData);
    } catch (error) {
      toast.error('Məlumat yüklənə bilmədi');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadImage(file);
      setFormData({...formData, image_url: url});
      toast.success('Şəkil yükləndi');
    } catch (error) {
      toast.error('Şəkil yüklənə bilmədi');
    } finally {
      setUploading(false);
    }
  };

  const handleMultipleImageUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      const uploadedUrls = [...formData.images];
      for (let i = 0; i < files.length; i++) {
        const url = await uploadImage(files[i]);
        uploadedUrls.push(url);
      }
      setFormData({...formData, images: uploadedUrls});
      toast.success(`${files.length} şəkil yükləndi`);
    } catch (error) {
      toast.error('Şəkillər yüklənə bilmədi');
    } finally {
      setUploading(false);
    }
  };

  const removeImage = (index) => {
    setFormData({...formData, images: formData.images.filter((_, i) => i !== index)});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingProduct) {
        await updateProduct(editingProduct.id, formData);
        toast.success('Məhsul yeniləndi');
      } else {
        await addProduct(formData);
        toast.success('Məhsul yaradıldı');
      }
      setDialogOpen(false);
      setEditingProduct(null);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error('Əməliyyat uğursuz oldu');
    }
  };

  const resetForm = () => {
    setFormData({ name: '', description: '', category_id: '', image_url: '', images: [], price: '', show_price: false, features: [], order: 0 });
    setFeatureInput('');
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      description: product.description || '',
      category_id: product.category_id,
      image_url: product.image_url || '',
      images: product.images || [],
      price: product.price || '',
      show_price: product.show_price || false,
      features: product.features || [],
      order: product.order || 0
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu məhsulu silmək istədiyinizə əminsiniz?')) return;
    try {
      await deleteProduct(id);
      toast.success('Məhsul silindi');
      fetchData();
    } catch (error) {
      toast.error('Silinmə uğursuz oldu');
    }
  };

  const addFeature = () => {
    if (featureInput.trim()) {
      setFormData({...formData, features: [...formData.features, featureInput.trim()]});
      setFeatureInput('');
    }
  };

  const removeFeature = (index) => {
    setFormData({...formData, features: formData.features.filter((_, i) => i !== index)});
  };

  const getCategoryName = (id) => categories.find(c => c.id === id)?.name || '-';

  if (loading) return <div className="animate-spin rounded-full h-8 w-8 border-4 border-[#1e3a5f] border-t-transparent mx-auto"></div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-[#1e3a5f]">Məhsullar</h1>
        <Button onClick={() => { setEditingProduct(null); resetForm(); setDialogOpen(true); }} className="bg-[#1e3a5f]">
          <Plus className="h-4 w-4 mr-2" /> Yeni Məhsul
        </Button>
      </div>

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Şəkil</TableHead>
              <TableHead>Ad</TableHead>
              <TableHead>Kateqoriya</TableHead>
              <TableHead>Qiymət</TableHead>
              <TableHead className="text-right">Əməliyyatlar</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {products.map((product) => (
              <TableRow key={product.id}>
                <TableCell>
                  <img src={product.image_url || 'https://pub-2603f7a1a1604332b276f2ff8fff86f5.r2.dev/polymax/placeholder.png'} alt="" className="w-12 h-12 object-cover rounded" />
                </TableCell>
                <TableCell className="font-medium">{product.name}</TableCell>
                <TableCell>{getCategoryName(product.category_id)}</TableCell>
                <TableCell>{product.show_price ? `${product.price} AZN` : '-'}</TableCell>
                <TableCell className="text-right space-x-2">
                  <Button variant="ghost" size="icon" onClick={() => handleEdit(product)}><Edit className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => handleDelete(product.id)} className="text-red-500"><Trash2 className="h-4 w-4" /></Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingProduct ? 'Məhsulu Redaktə Et' : 'Yeni Məhsul'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Ad *</Label>
                <Input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required />
              </div>
              <div className="space-y-2">
                <Label>Kateqoriya *</Label>
                <Select value={formData.category_id} onValueChange={(v) => setFormData({...formData, category_id: v})}>
                  <SelectTrigger><SelectValue placeholder="Seçin" /></SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Təsvir</Label>
              <Textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} />
            </div>
            <div className="space-y-2">
              <Label>Əsas Şəkil</Label>
              <div className="flex gap-2">
                <Input value={formData.image_url} onChange={(e) => setFormData({...formData, image_url: e.target.value})} placeholder="URL və ya yüklə" className="flex-1" />
                <Label className="cursor-pointer">
                  <div className={`px-4 py-2 bg-[#1e3a5f] text-white rounded-md flex items-center ${uploading ? 'opacity-50' : ''}`}>
                    <Upload className="h-4 w-4 mr-2" />
                    {uploading ? '...' : 'Yüklə'}
                  </div>
                  <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" disabled={uploading} />
                </Label>
              </div>
              {formData.image_url && <img src={formData.image_url} alt="Preview" className="mt-2 w-24 h-24 object-cover rounded" />}
            </div>
            <div className="space-y-2">
              <Label>Əlavə Şəkillər</Label>
              <Label className="cursor-pointer block">
                <div className={`px-4 py-3 border-2 border-dashed border-slate-300 rounded-lg text-center hover:border-[#1e3a5f] ${uploading ? 'opacity-50' : ''}`}>
                  <Upload className="h-6 w-6 mx-auto mb-2 text-slate-400" />
                  <span className="text-sm text-slate-500">Şəkillər seçin</span>
                </div>
                <input type="file" accept="image/*" multiple onChange={handleMultipleImageUpload} className="hidden" disabled={uploading} />
              </Label>
              {formData.images.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {formData.images.map((img, i) => (
                    <div key={i} className="relative group">
                      <img src={img} alt="" className="w-20 h-20 object-cover rounded" />
                      <button type="button" onClick={() => removeImage(i)} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100">
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Qiymət</Label>
                <Input value={formData.price} onChange={(e) => setFormData({...formData, price: e.target.value})} placeholder="Məs: 15.00" />
              </div>
              <div className="space-y-2">
                <Label>Sıra</Label>
                <Input type="number" value={formData.order} onChange={(e) => setFormData({...formData, order: parseInt(e.target.value) || 0})} />
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={formData.show_price} onCheckedChange={(v) => setFormData({...formData, show_price: v})} />
              <Label>Qiyməti göstər</Label>
            </div>
            <div className="space-y-2">
              <Label>Xüsusiyyətlər</Label>
              <div className="flex gap-2">
                <Input value={featureInput} onChange={(e) => setFeatureInput(e.target.value)} placeholder="Xüsusiyyət əlavə et" />
                <Button type="button" onClick={addFeature} variant="outline">Əlavə Et</Button>
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {formData.features.map((f, i) => (
                  <span key={i} className="bg-slate-100 px-3 py-1 rounded-full text-sm flex items-center gap-2">
                    {f}
                    <button type="button" onClick={() => removeFeature(i)} className="text-red-500"><X className="h-3 w-3" /></button>
                  </span>
                ))}
              </div>
            </div>
            <DialogFooter>
              <Button type="submit" className="bg-[#1e3a5f]"><Save className="h-4 w-4 mr-2" /> Yadda Saxla</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Banners Page
const BannersPage = () => {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({ title: '', subtitle: '', image_url: '', link: '', order: 0, is_active: true });

  useEffect(() => { fetchBanners(); }, []);

  const fetchBanners = async () => {
    try {
      const data = await getBanners();
      setBanners(data);
    } catch (error) {
      toast.error('Bannerlər yüklənə bilmədi');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadImage(file);
      setFormData({...formData, image_url: url});
      toast.success('Şəkil yükləndi');
    } catch (error) {
      toast.error('Şəkil yüklənə bilmədi');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingBanner) {
        await updateBanner(editingBanner.id, formData);
        toast.success('Banner yeniləndi');
      } else {
        await addBanner(formData);
        toast.success('Banner yaradıldı');
      }
      setDialogOpen(false);
      setEditingBanner(null);
      setFormData({ title: '', subtitle: '', image_url: '', link: '', order: 0, is_active: true });
      fetchBanners();
    } catch (error) {
      toast.error('Əməliyyat uğursuz oldu');
    }
  };

  const handleEdit = (banner) => {
    setEditingBanner(banner);
    setFormData({ title: banner.title, subtitle: banner.subtitle || '', image_url: banner.image_url, link: banner.link || '', order: banner.order || 0, is_active: banner.is_active });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu banneri silmək istədiyinizə əminsiniz?')) return;
    try {
      await deleteBanner(id);
      toast.success('Banner silindi');
      fetchBanners();
    } catch (error) {
      toast.error('Silinmə uğursuz oldu');
    }
  };

  if (loading) return <div className="animate-spin rounded-full h-8 w-8 border-4 border-[#1e3a5f] border-t-transparent mx-auto"></div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-[#1e3a5f]">Bannerlər</h1>
        <Button onClick={() => { setEditingBanner(null); setFormData({ title: '', subtitle: '', image_url: '', link: '', order: 0, is_active: true }); setDialogOpen(true); }} className="bg-[#1e3a5f]">
          <Plus className="h-4 w-4 mr-2" /> Yeni Banner
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {banners.map((banner) => (
          <Card key={banner.id} className="overflow-hidden">
            <div className="aspect-video bg-slate-100 relative">
              <img src={banner.image_url} alt={banner.title} className="w-full h-full object-cover" />
              {!banner.is_active && <div className="absolute top-2 right-2 bg-red-500 text-white text-xs px-2 py-1 rounded">Deaktiv</div>}
            </div>
            <CardContent className="p-4">
              <h3 className="font-semibold text-[#1e3a5f]">{banner.title}</h3>
              <p className="text-sm text-slate-500 mt-1">{banner.subtitle}</p>
              <div className="flex gap-2 mt-4">
                <Button variant="outline" size="sm" onClick={() => handleEdit(banner)}><Edit className="h-4 w-4 mr-1" /> Redaktə</Button>
                <Button variant="outline" size="sm" onClick={() => handleDelete(banner.id)} className="text-red-500"><Trash2 className="h-4 w-4 mr-1" /> Sil</Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingBanner ? 'Banneri Redaktə Et' : 'Yeni Banner'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Başlıq *</Label>
              <Input value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} required />
            </div>
            <div className="space-y-2">
              <Label>Alt Başlıq</Label>
              <Input value={formData.subtitle} onChange={(e) => setFormData({...formData, subtitle: e.target.value})} />
            </div>
            <div className="space-y-2">
              <Label>Şəkil *</Label>
              <div className="flex gap-2">
                <Input value={formData.image_url} onChange={(e) => setFormData({...formData, image_url: e.target.value})} required className="flex-1" />
                <Label className="cursor-pointer">
                  <div className={`px-4 py-2 bg-[#1e3a5f] text-white rounded-md flex items-center ${uploading ? 'opacity-50' : ''}`}>
                    <Upload className="h-4 w-4 mr-2" />
                    {uploading ? '...' : 'Yüklə'}
                  </div>
                  <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" disabled={uploading} />
                </Label>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Link</Label>
                <Input value={formData.link} onChange={(e) => setFormData({...formData, link: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Sıra</Label>
                <Input type="number" value={formData.order} onChange={(e) => setFormData({...formData, order: parseInt(e.target.value) || 0})} />
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={formData.is_active} onCheckedChange={(v) => setFormData({...formData, is_active: v})} />
              <Label>Aktiv</Label>
            </div>
            <DialogFooter>
              <Button type="submit" className="bg-[#1e3a5f]"><Save className="h-4 w-4 mr-2" /> Yadda Saxla</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Pages Editor
const PagesPage = () => {
  const [selectedPage, setSelectedPage] = useState('about');
  const [content, setContent] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const pages = [
    { id: 'about', name: 'Haqqımızda' },
  ];

  useEffect(() => { fetchContent(); }, [selectedPage]);

  const fetchContent = async () => {
    setLoading(true);
    try {
      const data = await getPageContent(selectedPage);
      setContent(data?.content || {});
    } catch (error) {
      toast.error('Məzmun yüklənə bilmədi');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updatePageContent(selectedPage, content);
      toast.success('Səhifə yadda saxlanıldı');
    } catch (error) {
      toast.error('Yadda saxlama uğursuz oldu');
    } finally {
      setSaving(false);
    }
  };

  const updateContent = (key, value) => {
    setContent({ ...content, [key]: value });
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadImage(file);
      updateContent('image_url', url);
      toast.success('Şəkil yükləndi');
    } catch (error) {
      toast.error('Şəkil yüklənə bilmədi');
    } finally {
      setUploading(false);
    }
  };

  if (loading) return <div className="animate-spin rounded-full h-8 w-8 border-4 border-[#1e3a5f] border-t-transparent mx-auto"></div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-[#1e3a5f]">Səhifə Məzmunu</h1>
        <Button onClick={handleSave} disabled={saving} className="bg-[#1e3a5f]">
          <Save className="h-4 w-4 mr-2" />
          {saving ? 'Saxlanılır...' : 'Yadda Saxla'}
        </Button>
      </div>

      <div className="flex gap-2 mb-6">
        {pages.map((page) => (
          <Button
            key={page.id}
            variant={selectedPage === page.id ? 'default' : 'outline'}
            onClick={() => setSelectedPage(page.id)}
            className={selectedPage === page.id ? 'bg-[#1e3a5f]' : ''}
          >
            {page.name}
          </Button>
        ))}
      </div>

      <Card>
        <CardContent className="p-6 space-y-4">
          {selectedPage === 'about' && (
            <>
              <div className="space-y-2">
                <Label>Başlıq</Label>
                <Input value={content.title || ''} onChange={(e) => updateContent('title', e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>Təsvir</Label>
                <Textarea value={content.description || ''} onChange={(e) => updateContent('description', e.target.value)} rows={4} />
              </div>
              <div className="space-y-2">
                <Label>Missiya</Label>
                <Textarea value={content.mission || ''} onChange={(e) => updateContent('mission', e.target.value)} rows={3} />
              </div>
              <div className="space-y-2">
                <Label>Əməkdaşlıq</Label>
                <Textarea value={content.collaboration || ''} onChange={(e) => updateContent('collaboration', e.target.value)} rows={4} placeholder="Əməkdaşlıq haqqında mətn" />
              </div>
              <div className="space-y-4">
                <Label>Xüsusiyyətlər (Niyə Bizi Seçməlisiniz) - JSON formatında</Label>
                <Textarea 
                  value={JSON.stringify(content.features || [], null, 2)} 
                  onChange={(e) => {
                    try { updateContent('features', JSON.parse(e.target.value)); } catch {}
                  }} 
                  rows={10}
                  className="font-mono text-sm"
                />
                <p className="text-xs text-slate-500">
                  Hər xüsusiyyət: {`{ "icon": "shield|truck|headphones|award|users|heart", "title": "...", "description": "..." }`}
                </p>
              </div>
              <div className="space-y-2">
                <Label>Şəkil</Label>
                <div className="flex gap-2">
                  <Input value={content.image_url || ''} onChange={(e) => updateContent('image_url', e.target.value)} placeholder="URL və ya yüklə" className="flex-1" />
                  <Label className="cursor-pointer">
                    <div className={`px-4 py-2 bg-[#1e3a5f] text-white rounded-md flex items-center ${uploading ? 'opacity-50' : ''}`}>
                      <Upload className="h-4 w-4 mr-2" />
                      {uploading ? '...' : 'Yüklə'}
                    </div>
                    <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" disabled={uploading} />
                  </Label>
                </div>
                {content.image_url && <img src={content.image_url} alt="Preview" className="mt-2 w-32 h-32 object-cover rounded" />}
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Contact Info Page
const ContactInfoPage = () => {
  const [info, setInfo] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchInfo(); }, []);

  const fetchInfo = async () => {
    try {
      const data = await getContactInfo();
      setInfo(data);
    } catch (error) {
      toast.error('Məlumat yüklənə bilmədi');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateContactInfo(info);
      toast.success('Əlaqə məlumatı yadda saxlanıldı');
    } catch (error) {
      toast.error('Yadda saxlama uğursuz oldu');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="animate-spin rounded-full h-8 w-8 border-4 border-[#1e3a5f] border-t-transparent mx-auto"></div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-[#1e3a5f]">Əlaqə Məlumatı</h1>
        <Button onClick={handleSave} disabled={saving} className="bg-[#1e3a5f]">
          <Save className="h-4 w-4 mr-2" />
          {saving ? 'Saxlanılır...' : 'Yadda Saxla'}
        </Button>
      </div>

      <Card>
        <CardContent className="p-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Telefon</Label>
              <Input value={info.phone || ''} onChange={(e) => setInfo({...info, phone: e.target.value})} />
            </div>
            <div className="space-y-2">
              <Label>WhatsApp</Label>
              <Input value={info.whatsapp || ''} onChange={(e) => setInfo({...info, whatsapp: e.target.value})} />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Email</Label>
            <Input value={info.email || ''} onChange={(e) => setInfo({...info, email: e.target.value})} />
          </div>
          <div className="space-y-2">
            <Label>Ünvan</Label>
            <Textarea value={info.address || ''} onChange={(e) => setInfo({...info, address: e.target.value})} />
          </div>
          <div className="space-y-2">
            <Label>İş Saatları</Label>
            <Input value={info.working_hours || ''} onChange={(e) => setInfo({...info, working_hours: e.target.value})} />
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Messages Page
const MessagesPage = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchMessages(); }, []);

  const fetchMessages = async () => {
    try {
      const data = await getContactMessages();
      setMessages(data);
    } catch (error) {
      toast.error('Mesajlar yüklənə bilmədi');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkRead = async (id) => {
    try {
      await markMessageRead(id);
      fetchMessages();
    } catch (error) {
      toast.error('Əməliyyat uğursuz oldu');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu mesajı silmək istədiyinizə əminsiniz?')) return;
    try {
      await deleteContactMessage(id);
      toast.success('Mesaj silindi');
      fetchMessages();
    } catch (error) {
      toast.error('Silinmə uğursuz oldu');
    }
  };

  if (loading) return <div className="animate-spin rounded-full h-8 w-8 border-4 border-[#1e3a5f] border-t-transparent mx-auto"></div>;

  return (
    <div>
      <h1 className="text-2xl font-bold text-[#1e3a5f] mb-6">Mesajlar</h1>

      {messages.length === 0 ? (
        <Card><CardContent className="p-8 text-center text-slate-500">Heç bir mesaj yoxdur</CardContent></Card>
      ) : (
        <div className="space-y-4">
          {messages.map((msg) => (
            <Card key={msg.id} className={!msg.is_read ? 'border-l-4 border-l-[#3b82f6]' : ''}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="font-semibold text-[#1e3a5f]">{msg.name}</span>
                      {!msg.is_read && <span className="bg-[#3b82f6] text-white text-xs px-2 py-0.5 rounded">Yeni</span>}
                    </div>
                    <p className="text-sm text-slate-500 mb-1">{msg.email} {msg.phone && `• ${msg.phone}`}</p>
                    <p className="text-slate-700 mt-2">{msg.message}</p>
                    <p className="text-xs text-slate-400 mt-2">{new Date(msg.created_at).toLocaleString('az-AZ')}</p>
                  </div>
                  <div className="flex gap-2">
                    {!msg.is_read && (
                      <Button variant="ghost" size="icon" onClick={() => handleMarkRead(msg.id)} title="Oxundu işarələ">
                        <Eye className="h-4 w-4" />
                      </Button>
                    )}
                    <Button variant="ghost" size="icon" onClick={() => handleDelete(msg.id)} className="text-red-500">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

// Main Admin Layout
const AdminDashboard = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      
      <div className="lg:ml-64">
        <header className="bg-white border-b sticky top-0 z-30">
          <div className="flex items-center justify-between px-4 py-3">
            <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setSidebarOpen(true)}>
              <Menu className="h-6 w-6" />
            </Button>
            <div className="flex-1" />
          </div>
        </header>

        <main className="p-6">
          <Routes>
            <Route index element={<DashboardPage />} />
            <Route path="categories" element={<CategoriesPage />} />
            <Route path="products" element={<ProductsPage />} />
            <Route path="banners" element={<BannersPage />} />
            <Route path="pages" element={<PagesPage />} />
            <Route path="contact" element={<ContactInfoPage />} />
            <Route path="messages" element={<MessagesPage />} />
          </Routes>
        </main>
      </div>
    </div>
  );
};

export default AdminDashboard;
